import { Icon } from "@iconify/react";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Image,
  Input,
  message,
  Radio,
  RadioChangeEvent,
  Select,
  TimePicker,
  Tooltip,
  Upload,
} from "antd";
import { get, isEmpty } from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { SketchPicker } from "react-color";
import { Controller, FieldValues, RegisterOptions, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import {
  fetchMouleSelectionFaqQandA,
  fetchMouleSelectionMyairports,
  fetchMouleSelectionPrivacy,
  fetchMouleSelectionPromotion,
} from "~/Modules/Centralized/Forms/AirlineImages/FetchModule";
import { Loader } from "../Loader/Loader";
import ConfirmationModal from "../Modal/confirmation-modal";
import RichTextEditor from "../Richtext/Richtext";
import dialCodeToIso from "./DialCodeToISO";
import DynamicFormArray from "./DynamicFormArray";

const { TextArea } = Input;
const { Option } = Select;

export type FieldType =
  | "mobile"
  | "input"
  | "textarea"
  | "checkbox"
  | "file"
  | "select"
  | "date"
  | "richText"
  | "number"
  | "time"
  | "dateTime"
  | "dateFormat"
  | "multicheckbox"
  | "radio"
  | "radioWithDropdown"
  | "color"
  | "dropdownWithInput"
  | "multiFile"
  | "dynamicRow"
  | "dateWithCheckbox"
  | ""
  | "multiSelect"
  | "multiInput"
  | "inputNotes";

export interface OptionType {
  value: string | number;
  label: string;
}

export interface FieldConfig {
  name: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  validation?: RegisterOptions;
  options?: OptionType[];
  className?: string;
  multipleFile?: boolean;
  loadOptions?: (selectedModule?: any, currentAirport?: any) => Promise<OptionType[]>;
  editable?: boolean;
  onChange?: any;
  onSearch?: any;
  hidden?: boolean;
  usedFileType?: string;
  note?: string;
  futureDateRestriction?: boolean;
  rowConfig?: DynamicRowConfig[];
  dynamicFormType?: string;
  fieldName?: string;
  fieldValue?: string;
  formType?: string;
  showField?: (formValues: any) => boolean;
  imageDimension?: {
    width: number;
    height: number;
  };
  openUpload?: (fieldName: string, setValue: (name: string, value: string) => void) => void;
  uploadTrigger?: boolean;
  disabledPreviousDateTime?: boolean;
}

export interface DynamicRowConfig {
  fields: FieldConfig[];
  minRows?: number;
  maxRows?: number;
}

export interface DynamicRowData {
  id: string;
  [key: string]: any;
}

export interface FormConfig {
  initialValues: FieldValues;
  fields: FieldConfig[];
}

export interface DynamicFormProps {
  config: FormConfig;
  isLoading?: boolean;
  onSubmit?: (data: FieldValues, buttonValue: "save" | "submit" | "submitWithNoReview", reviewNotes?: string) => void;
  previewContent?: any;
  CreatingNewOne?: boolean;
  finalData?: (data: FieldValues) => void;
  hideButton?: boolean;
  defaultReviewNotes?: string;
  enableDynamicWatch?: boolean;
}

const getBase64 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

const isValidPhoneNumber = (phoneNumber: any) => {
  if (!phoneNumber) return true;

  const cleanNumber = phoneNumber?.replace?.(/[^\d+]/g, "");
  const hasCountryCode = cleanNumber.startsWith("+");

  if (!hasCountryCode) return false;
  const numberLength = cleanNumber.length - (cleanNumber.indexOf("+") + 1);
  return numberLength >= 7 && numberLength <= 15;
};

// Utility to create a Blob URL from a File object
const createBlobUrl = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const blob = new Blob([file], { type: file.type });
    const url = URL.createObjectURL(blob);
    resolve(url);
  });
};

// Utility to revoke Blob URL when no longer needed
const revokeBlobUrl = (url: string) => {
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

export const DynamicForm: React.FC<DynamicFormProps> = ({
  config,
  onSubmit,
  isLoading,
  previewContent,
  CreatingNewOne,
  finalData,
  hideButton,
  defaultReviewNotes = "",
  enableDynamicWatch = false,
}) => {
  const getContainer = (triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement;
  if (isLoading) {
    return <Loader />;
  }
  const FlagIcon: React.FC<any> = ({ countryCode }) => (
    <img
      src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
      alt={`${countryCode} flag`}
      className="w-6 h-4 object-cover"
    />
  );
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [formData, setFormData] = useState<FieldValues | null>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [button, setButton] = useState<"save" | "submit" | "submitWithNoReview">("save");
  const [options, setOptions] = useState<Record<string, OptionType[]>>({});
  const [isPreviewVisible, setIsPreviewVisible] = useState(!!previewContent);
  const [currentAirport] = useLocalStorage("currentAirport", null);
  const [fetchingDuration, setFetchingDuration] = useState(false);
  console.log("dynamicForm", config.fields, config.initialValues);
  console.log("lastOptions", options, confirmModalVisible);

  useEffect(() => {
    const loadDynamicOptions = async () => {
      const fieldsWithLoadOptions = config?.fields?.filter((field) => field?.loadOptions);

      for (const field of fieldsWithLoadOptions) {
        try {
          const dynamicOptions = await field?.loadOptions?.();
          setOptions((prevOptions) => ({
            ...prevOptions,
            [field.name]: dynamicOptions,
          }));
        } catch (error) {
          console.error(`Error loading options for ${field.name}:`, error);
        }
      }
    };

    loadDynamicOptions();
  }, [config?.fields]);

  const modules = {
    toolbar: [
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [
        { color: ["#000000", "#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#FFFFFF", "#000000"] },
        { background: ["#FFFFFF", "#FFDDDD", "#DDFFDD", "#DDDDFF", "#FFFFDD", "#FFDDFF", "#DDFFFF", "#000000"] },
      ],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [false, "center", "right", "justify"] }],
      ["link", "image", "video"],
      ["blockquote", "code-block"],
      [{ divider: "" }],
      // ['clean'],
    ],
  };

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    watch,
    trigger,
    reset,
  } = useForm({
    defaultValues: config.initialValues,
  });
  const watchCardType = watch("epcType");
  console.log("watchCardType", watchCardType);
  useEffect(() => {
    if (!isLoading) {
      reset(config.initialValues);
    }
  }, [config.initialValues, reset, isLoading]);
  if (isLoading) {
    return <Loader />;
  }
  const [reviewNotes, setReviewNotes] = useState("");
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const filteredCountries = Object.entries(dialCodeToIso).filter(([code, iso]) => {
    const searchString = searchQuery.toLowerCase();
    const countryCode = code.toLowerCase();
    const countryIso = iso.toLowerCase();

    // Search by country code (including or excluding +)
    const codeMatches = countryCode.includes(searchString) || countryCode?.replace("+", "").includes(searchString);

    // Search by country ISO code
    const isoMatches = countryIso.includes(searchString);

    return codeMatches || isoMatches;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const selectedModule: any = watch("field_module");
    if (!selectedModule) return;

    const fetchDynamicOptions = async () => {
      const fieldsWithLoadOptions = config?.fields?.filter((field) => typeof field?.loadOptions === "function");

      for (const field of fieldsWithLoadOptions) {
        try {
          const dynamicOptions = await field?.loadOptions(selectedModule);
          setOptions((prevOptions) => ({
            ...prevOptions,
            [field?.name]: dynamicOptions,
          }));
        } catch (error) {
          console.error(`Error loading options for ${field?.name}:`, error);
        }
      }
    };

    fetchDynamicOptions();
  }, [watch("field_module")]);
  useEffect(() => {
    const zone = getValues("zone") || watch("zone");

    const field = config?.fields?.find((f) => f?.name === "zoneLevel");
    if (!field?.loadOptions) return;

    field
      .loadOptions(zone, currentAirport)
      .then((options) => {
        setOptions((prev) => ({ ...prev, zoneLevel: options || [] }));

        const current = watch("zoneLevel");
        if (current && !options?.some((o: any) => o.value === current)) {
          setValue("zoneLevel", "");
        }
      })
      .catch(() => {
        setOptions((prev) => ({ ...prev, zoneLevel: [] }));
        setValue("zoneLevel", "");
      });
  }, [watch("zone"), getValues("zone"), currentAirport, config?.fields]);
  // Add this state to track the editable state of published field
  const [publishedEditable, setPublishedEditable] = useState<boolean>();

  useEffect(() => {
    if (!enableDynamicWatch) return;
    const airportField = watch("field_project_airport");
    const locationFieldIndex = config?.fields?.findIndex((field) => field.name === "location_code");
    const newEditable = !!airportField;
    if (locationFieldIndex !== -1 && locationFieldIndex !== undefined) {
      config.fields[locationFieldIndex].editable = newEditable;
      setPublishedEditable(newEditable);
    }
  }, [watch("field_project_airport"), enableDynamicWatch]);

  useEffect(() => {
    if (!enableDynamicWatch) return;
    const startDate = watch("start_date");
    const endDate = watch("end_date");
    const publishedFieldIndex = config?.fields?.findIndex((field) => field.name === "published");
    const newEditable = !(startDate || endDate);
    if (publishedFieldIndex !== -1 && publishedFieldIndex !== undefined) {
      config.fields[publishedFieldIndex].editable = newEditable;
      setPublishedEditable(newEditable);
    }
  }, [watch("start_date"), watch("end_date"), enableDynamicWatch]);

  const [privacyModuleOptions, setPrivacyModuleOptions] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const data = await fetchMouleSelectionPrivacy();
      setPrivacyModuleOptions(data);
    };
    load();
  }, []);

  const privacyAirport = watch("project_airport_id_1");

  const selectedOptions = privacyModuleOptions.filter((option) =>
    Array.isArray(privacyAirport) ? privacyAirport.includes(option.value) : false
  );

  const disableTitleField =
    selectedOptions.length > 0 && selectedOptions.every((option) => option.slug?.toLowerCase().startsWith("myairport"));

  const disablePdf =
    selectedOptions.length > 0 && selectedOptions.every((option) => option.slug?.toLowerCase().startsWith("corporate"));

  const [promotionsOptions, setPromotionsOptions] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const data = await fetchMouleSelectionPromotion();
      setPromotionsOptions(data);
    };
    load();
  }, []);

  const promotionAirport = watch("project_airport_id");

  const selectedPromoOptions = promotionsOptions.filter((option) =>
    Array.isArray(promotionAirport) ? promotionAirport.includes(option.value) : false
  );

  const disableRedirectField =
    selectedPromoOptions.length > 0 &&
    selectedPromoOptions.every((option) => option.slug && option.slug?.toLowerCase().startsWith("eraman"));

  const [faqOptions, setFaqOptions] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const data = await fetchMouleSelectionFaqQandA();
      setFaqOptions(data);
    };
    load();
  }, []);

  const faqAirport = watch("module_name");

  const selectedFaqOptions = faqOptions.filter((option) =>
    Array.isArray(faqAirport) ? faqAirport.includes(option.value) : false
  );

  const disableCatFaqField =
    selectedFaqOptions.length > 0 &&
    selectedFaqOptions.every(
      (option) =>
        option.slug && (option.slug.toLowerCase().startsWith("eraman") || option.slug.toLowerCase().startsWith("magp"))
    );

  const [myairportsOptions, setmyairportsOptions] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      const data = await fetchMouleSelectionMyairports();
      setmyairportsOptions(data);
    };
    load();
  }, []);

  const myairports = watch("project_airport_id");

  const selectedmyairportsOptions = myairportsOptions.filter((option) => {
    const match = Array.isArray(myairports) ? myairports.includes(option.value) : myairports === option.value;

    return match;
  });

  const disablemyairportsField =
    selectedmyairportsOptions.length > 0 &&
    selectedmyairportsOptions.every((option) => {
      const result =
        option.slug &&
        (option.slug.toLowerCase().includes("eraman") || option.slug.toLowerCase().includes("passenger"));

      return result;
    });

  const disableLongDescriptionField =
    selectedmyairportsOptions.length > 0 &&
    selectedmyairportsOptions.every((option) => {
      return option.slug && option.slug.toLowerCase().includes("passenger");
    });

  const disableNavigationLink =
    selectedmyairportsOptions.length === 1 &&
    selectedmyairportsOptions[0]?.slug &&
    selectedmyairportsOptions[0].slug.toLowerCase().includes("eraman");

  useEffect(() => {
    if (disableTitleField) {
      setValue("tab_title_english", "");
      setValue("tab_title_malay", "");
      setValue("tab_title_mandarin", "");
    }

    if (disablePdf) {
      setValue("pdf_url", "");
    }

    if (disableRedirectField) {
      setValue("redirect_url", "");
    }

    if (disableCatFaqField) {
      setValue("key_info_type", "");
    }

    if (disablemyairportsField) {
      setValue("repeat_for_days", "");
      setValue("frequency_count", "");
      setValue("frequency_time", "");
      setValue("location", "");
      setValue("offer", "");
      setValue("notification_type", "");
      setValue("repeat_type", "");
      setValue("type", "");
      setValue("shop_category", "");
      setValue("image", "");
      setValue("square_image", "");
    }
    if (disableNavigationLink) {
      setValue("navigation_link", "");
    }
    if (disableLongDescriptionField) {
      setValue("long_description", "");
      setValue("long_description_malay", "");
      setValue("long_description_mandarin", "");
    }
  }, [
    disableTitleField,
    disablePdf,
    disableRedirectField,
    disableCatFaqField,
    disablemyairportsField,
    disableNavigationLink,
    disableLongDescriptionField,
    setValue,
  ]);

  useEffect(() => {
    const zone = getValues("centralize_zone") || watch("centralize_zone");

    const field = config?.fields?.find((f) => f?.name === "centralize_zone_level");
    if (!field?.loadOptions) return;

    field
      .loadOptions(zone, "")
      .then((options) => {
        setOptions((prev) => ({ ...prev, centralize_zone_level: options || [] }));
        const current = watch("centralize_zone_level");
        if (current && !options?.some((o: any) => o.value === current)) {
          setValue("centralize_zone_level", "");
        }
      })
      .catch(() => {
        setOptions((prev) => ({ ...prev, centralize_zone_level: [] }));
        setValue("centralize_zone_level", "");
      });
  }, [watch("centralize_zone"), getValues("centralize_zone"), config?.fields]);

  useEffect(() => {
    const airportId = getValues("field_project_airport") || watch("field_project_airport");

    const field = config?.fields?.find((f) => f?.name === "location_code");
    if (!field?.loadOptions) return;

    field
      .loadOptions(airportId)
      .then((options) => {
        setOptions((prev) => ({
          ...prev,
          location_code: options || [],
        }));

        const current = watch("location_code");
        if (current && !options?.some((o: any) => o.value === current)) {
          setValue("location_code", "");
        }
      })
      .catch(() => {
        setOptions((prev) => ({
          ...prev,
          location_code: [],
        }));
        setValue("location_code", "");
      });
  }, [watch("field_project_airport"), getValues("field_project_airport"), config?.fields]);

  const handleNumericInput = (e: any) => {
    e.target.value = e.target.value?.replace?.(/[^0-9]/g, "");
  };

  const formatPhoneNumber = (value: string | null | undefined) => {
    console.log("formatPhoneNumber", value);
    if (!value) return "";

    if (value?.startsWith("null")) {
      const number = value?.replace("null", "").trim();
      return `+60${number}`;
    }

    if (value.startsWith("undefined")) {
      const number = value?.replace("undefined", "").trim();
      return `+60${number}`;
    }

    if (value.includes(" ")) {
      return value?.replace(" ", "");
    }

    return value;
  };

  const watchEpcType = watch("epcType");

  useEffect(() => {
    if (watchEpcType) {
      trigger("passportNumber");
    }
  }, [watchEpcType, trigger]);

  const handleDynamicFormArrayChange = (fieldName: string) => (data: { rows: any[]; rowConfig: any }) => {
    const formattedRows = data.rows.map((row, index) => {
      const rowData = { ...row };
      const configFields = data.rowConfig[index]?.fields || [];
      configFields.forEach((field: FieldConfig) => {
        const propertyName = field.name.split(".").pop();
        if (row[propertyName!] !== undefined) {
          rowData[propertyName!] = row[propertyName!];
        }
      });

      return rowData;
    });

    console.log("Formatted Rows:", formattedRows, fieldName, data);
    setValue(fieldName, formattedRows);
  };

  const renderField = (field: FieldConfig) => {
    console.log("renderFieldddddddddd", field);
    if (field.showField) {
      const currentValues = getValues();
      if (!field.showField(currentValues)) {
        return null;
      }
    }

    const isTitleField = ["tab_title_english", "tab_title_malay", "tab_title_mandarin"].includes(field.name);
    const isPdf = field.name === "pdf_url";
    const isRedirectField = field.name === "redirect_url";
    const isKeyInfoField = field.name === "key_info_type";

    const isMyairportFields = [
      "repeat_for_days",
      "frequency_count",
      "frequency_time",
      "location",
      "offer",
      "notification_type",
      "repeat_type",
      "type",
      "shop_category",
      "image",
      "square_image",
    ].includes(field.name);

    const isLongDescriptionField = ["long_description", "long_description_malay", "long_description_mandarin"].includes(
      field.name
    );

    const isNavigation = field.name === "navigation_link";

    const shouldDisable =
      (isTitleField && disableTitleField) ||
      (isRedirectField && disableRedirectField) ||
      (isKeyInfoField && disableCatFaqField) ||
      (isMyairportFields && disablemyairportsField) ||
      (isNavigation && disableNavigationLink) ||
      (isLongDescriptionField && disableLongDescriptionField) ||
      (isPdf && disablePdf);

    switch (field.type) {
      case "input":
        return (
          <Controller
            name={field.name}
            control={control}
            // rules={field.validation}

            rules={{
              ...field.validation,
              required: shouldDisable ? false : field.validation?.required,
            }}
            render={({ field: controllerField }: { field: FieldValues }) => {
              return (
                <>
                  <Input
                    {...controllerField}
                    onBlur={async (e) => {
                      const trimmedValue = e.target.value.trim();
                      controllerField.onChange(trimmedValue);

                      // Auto-fill duration from YouTube link
                      if (field.name === "video_link" && trimmedValue) {
                        setFetchingDuration(true);
                        try {
                          const { fetchYoutubeDuration } = await import(
                            "~/Modules/ECMS/PASSENGER/homepage/Homepagemanagment.drilldownData"
                          );
                          const duration = await fetchYoutubeDuration(trimmedValue);
                          if (duration) {
                            setValue("duration", duration);
                            message.success(`Duration auto-filled: ${duration}`);
                          } else {
                            message.warning("Could not fetch duration. Please enter manually.");
                          }
                        } catch (error) {
                          console.error("Failed to fetch YouTube duration:", error);
                          message.error("Failed to fetch duration. Please enter manually.");
                        } finally {
                          setFetchingDuration(false);
                        }
                      }
                    }}
                    placeholder={field.placeholder}
                    // disabled={field.editable === false || (field.name === "duration" && fetchingDuration)}
                    disabled={
                      field.editable === false || (field.name === "duration" && fetchingDuration) || shouldDisable
                    }
                    maxLength={field?.name === "nric" ? 12 : null}
                    type={field?.name === "nric" ? "number" : null}
                    className="h-10"
                    suffix={
                      field.name === "duration" && fetchingDuration ? (
                        <Icon
                          icon="eos-icons:loading"
                          className="animate-spin"
                        />
                      ) : null
                    }
                  />
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );

      case "inputNotes":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }) => {
              return (
                <>
                  <Input
                    {...controllerField}
                    onBlur={(e) => {
                      const trimmedValue = e.target.value.trim();
                      setValue(field.name, trimmedValue);
                      controllerField.onChange(trimmedValue);
                    }}
                    placeholder={field.placeholder}
                    disabled={field.editable === false}
                    className="h-10"
                    suffix={
                      field.name === "duration" && fetchingDuration ? (
                        <Icon
                          icon="eos-icons:loading"
                          className="animate-spin"
                        />
                      ) : null
                    }
                  />

                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );

      case "multiInput":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField, fieldState: { error } }: { field: FieldValues; fieldState: any }) => (
              <>
                <Select
                  mode="tags"
                  value={controllerField.value || []}
                  onChange={(value) => controllerField.onChange(value)}
                  placeholder={field.placeholder || "Type and press Enter to add tags"}
                  disabled={field.editable === false}
                  open={false}
                  className="h-auto min-h-[40px] w-full"
                  suffixIcon={
                    <Tooltip title="Type and press Enter to add tags">
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                    </Tooltip>
                  }
                />
                {/* {error && (
                  <div style={{ marginTop: "8px", color: "#ff4d4f" }}>
                    {error.message}
                  </div>
                )} */}
                {field.note && (
                  <div
                    style={{
                      marginTop: "8px",
                      color: "#666",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: "6px",
                    }}
                  >
                    <Icon
                      icon="mdi:information-outline"
                      color="#666"
                      width="16"
                      height="16"
                    />
                    <span>{field.note}</span>
                  </div>
                )}
              </>
            )}
          />
        );

      case "mobile":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...field.validation,
            }}
            render={({ field: controllerField }) => {
              const value = formatPhoneNumber(controllerField.value) || "";
              console.log("movileVlaue", value, controllerField.value);

              return (
                <>
                  <PhoneInput
                    country={"my"}
                    value={value}
                    onChange={(value, country: any, e, formattedValue) => {
                      console.log("valuessssssssss", value, country, e, formattedValue);

                      if (
                        !value ||
                        value.toLowerCase().startsWith("null") ||
                        value.toLowerCase().startsWith("undefined") ||
                        value === `+${country?.dialCode}null` ||
                        value === `+${country?.dialCode}undefined`
                      ) {
                        console.log("movileNumber uhhhh", field.validation?.required, value);
                        const formattedPhone = country?.dialCode
                          ? `+${country.dialCode} ${value?.replace(/^null/i, "") || ""}`
                          : ""; // No default country code
                        controllerField.onChange(formattedPhone);
                        if (field.validation?.required && (!value || value.toLowerCase() === "null")) {
                          controllerField.onChange("");
                        }
                        return;
                      }

                      // Clean the value by removing any spaces
                      const cleanValue = value?.replace?.(/\s+/g, "");

                      // Remove country code (dialCode) from the cleaned value
                      const countryCode = country?.dialCode || ""; // No default "60"
                      const number = cleanValue?.replace?.(new RegExp(`^\\+?${countryCode}`), "").trim();

                      console.log("mobileNumber", field.validation?.required, number);

                      // Check if there's an actual number after removing country code
                      if (field.validation?.required && (!number || number === "")) {
                        controllerField.onChange(""); // Trigger required validation
                      } else {
                        // Format the final phone number
                        const formattedPhone = countryCode ? `+${countryCode} ${number}` : number; // Only format if country is selected
                        controllerField.onChange(formattedPhone);
                      }
                    }}
                    // inputProps={{
                    //   name: field.name,
                    //   required: !!field.validation?.required,
                    //   autoFocus: true
                    // }}
                    onBlur={controllerField.onBlur} // Ensure validation triggers on blur
                    enableSearch
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                    }}
                    dropdownStyle={{
                      zIndex: 1000,
                    }}
                    disabled={field.editable === false}
                  />

                  {field.note && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
      case "number":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <Input
                {...controllerField}
                type="text"
                className="h-10"
                placeholder={field.placeholder}
                onInput={handleNumericInput}
                maxLength={field.name === "nric" ? 12 : null}
              />
            )}
          />
        );
      case "textarea":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <TextArea
                {...controllerField}
                placeholder={field.placeholder}
              />
            )}
          />
        );
      case "checkbox":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              // <Checkbox {...controllerField}>{field.label}</Checkbox>
              <Checkbox
                {...controllerField}
                // checked={!!controllerField.value}
                checked={controllerField.value === "True" || controllerField.value === true}
                disabled={field.editable === false}
                onChange={(e) => controllerField.onChange(e.target.checked)}
              >
                {field.label}
              </Checkbox>
            )}
          />
        );
      case "file":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...field.validation,

              // Make required false when disabled
              required: shouldDisable ? false : field.validation?.required,

              validate: (value) => {
                // Skip validation if disabled
                if (shouldDisable) {
                  return true;
                }

                if (!field.validation) return true;

                if (
                  field?.validation?.required &&
                  (!value || value?.length === 0 || !(value[0]?.url || value[0]?.originFileObj))
                ) {
                  return `${field.label} is required`;
                }

                const fileObj = value[0]?.originFileObj || value[0];
                const fileType = fileObj?.type?.toLowerCase();
                const fileName = fileObj?.name?.toLowerCase() || "";

                const isImage =
                  fileType?.startsWith("image/") ||
                  fileName?.endsWith(".png") ||
                  fileName?.endsWith(".jpg") ||
                  fileName?.endsWith(".jpeg") ||
                  fileName?.endsWith(".gif") ||
                  fileName?.endsWith(".bmp") ||
                  fileName?.endsWith(".webp") ||
                  fileName?.endsWith(".tiff") ||
                  fileName?.endsWith(".jfif") ||
                  fileName?.endsWith(".svg");

                const isVideo =
                  fileType?.startsWith("video/") ||
                  fileName.endsWith(".mp4") ||
                  fileName.endsWith(".mov") ||
                  fileName.endsWith(".avi") ||
                  fileName.endsWith(".mkv");

                const isPdf = fileType === "application/pdf" || fileName.endsWith(".pdf");

                const maxWidth = field.imageDimension?.width || 1920;
                const maxHeight = field.imageDimension?.height || 800;

                if (isImage && fileObj?.size > 2 * 1024 * 1024) {
                  return `Please add image under 2 MB and dimension ${maxWidth}x${maxHeight}`;
                }

                if (isVideo && fileObj?.size > 80 * 1024 * 1024) {
                  return "Please add video under 80 MB";
                }

                if (isPdf && fileObj?.size > 5 * 1024 * 1024) {
                  return "Please add PDF under 5 MB";
                }

                return true;
              },
            }}
            render={({ field: controllerField, fieldState }) => {
              const hasFile =
                controllerField?.value &&
                controllerField?.value?.length > 0 &&
                (controllerField?.value?.[0]?.url || controllerField?.value?.[0]?.originFileObj);

              const file = controllerField?.value?.[0];
              const fileType = file?.type?.toLowerCase();
              const fileName = file?.name || "file";
              const maxWidth = field.imageDimension?.width || 1920;
              const maxHeight = field.imageDimension?.height || 800;
              const fieldContainerRef = useRef<HTMLDivElement>(null);
              const [dimensions, setDimensions] = React.useState<{ width: number; height: number } | null>(null);
              const [readableSize, setReadableSize] = React.useState<string | null>(null);
              useEffect(() => {
                if (!fieldState?.error || !field.validation?.required) return;

                const firstEmptyRequiredField = Object.entries(control._formValues).find(([key, value]) => {
                  const fieldDef = config.fields.find((f: FieldConfig) => f.name === key);
                  if (!fieldDef?.validation?.required) return false;

                  if (fieldDef.type === "richText") {
                    const val = Array.isArray(value) ? value.join(", ") : value || "";
                    const strippedVal = String(val)
                      ?.replace(/<[^>]*>/g, "")
                      ?.trim();
                    return !strippedVal;
                  }
                  if (fieldDef.type === "file" || fieldDef.type === "multiFile") {
                    const list = Array.isArray(value) ? value : [];
                    const first = list[0];
                    return !list.length || !(first?.url || first?.originFileObj);
                  }
                  if (Array.isArray(value)) return value.length === 0;
                  return value === undefined || value === null || String(value).trim() === "";
                });

                if (firstEmptyRequiredField && firstEmptyRequiredField[0] === field.name) {
                  fieldContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }, [fieldState?.error, control._formValues]);

              const isImage =
                fileType?.startsWith("image/") ||
                fileName?.endsWith(".png") ||
                fileName?.endsWith(".jpg") ||
                fileName?.endsWith(".jpeg") ||
                fileName?.endsWith(".gif") ||
                fileName?.endsWith(".bmp") ||
                fileName?.endsWith(".webp") ||
                fileName?.endsWith(".tiff") ||
                fileName?.endsWith(".jfif") ||
                fileName?.endsWith(".svg");

              const isVideo =
                fileType?.startsWith("video/") ||
                fileName?.toLowerCase().endsWith(".mp4") ||
                fileName?.toLowerCase().endsWith(".mov") ||
                fileName?.toLowerCase().endsWith(".avi") ||
                fileName?.toLowerCase().endsWith(".mkv");

              const isPdf = fileType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf");
              const usedFileType = field?.usedFileType?.toLowerCase() || "";
              const isImageField = usedFileType.includes("image");
              const isVideoField = usedFileType.includes("video");
              const isPdfField = usedFileType.includes("pdf");
              const formatBytes = (bytes?: number | null) => {
                if (bytes === undefined || bytes === null) return "Unknown";
                if (bytes === 0) return "0 KB";
                const k = 1024;
                const sizes = ["B", "KB", "MB", "GB"];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
              };

              const handleDelete = () => {
                if (file?.preview) URL.revokeObjectURL(file.preview);
                setValue(field.name, "", { shouldValidate: true }); // Set to "" instead of []
                setReadableSize(null);
                setDimensions(null);
                trigger(field.name);
              };

              const renderFile = () => {
                const fileSrc = file?.url || file?.preview;
                const deleteIcon = (
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "-10px",
                      backgroundColor: "rgba(0,0,0,0.65)",
                      borderRadius: "50%",
                      padding: "6px",
                      cursor: "pointer",
                      zIndex: 5,
                    }}
                    onClick={handleDelete}
                    title="Remove file"
                  >
                    <Icon
                      icon="mdi:close"
                      color="#fff"
                      width="14"
                      height="14"
                    />
                  </div>
                );

                if (isImage) {
                  return (
                    <div style={{ display: "inline-block", position: "relative", marginRight: "18px" }}>
                      <div
                        style={{
                          padding: "10px",
                          border: "1px solid #e6e6e6",
                          borderRadius: "8px",
                          background: "#fff",
                        }}
                      >
                        <img
                          src={fileSrc}
                          alt={fileName}
                          style={{ maxWidth: "220px", maxHeight: "220px", display: "block", borderRadius: "6px" }}
                          onLoad={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                          }}
                        />
                      </div>
                      {deleteIcon}
                    </div>
                  );
                }

                if (isVideo) {
                  return (
                    <div style={{ display: "inline-block", position: "relative", marginRight: "18px" }}>
                      <div style={{ border: "1px solid #e6e6e6", borderRadius: "8px", background: "#fff" }}>
                        <video
                          controls
                          src={fileSrc}
                          style={{ maxWidth: "300px", borderRadius: "6px" }}
                          onLoadedMetadata={(e) => {
                            const v = e.currentTarget as HTMLVideoElement;
                            if (v.videoWidth && v.videoHeight)
                              setDimensions({ width: v.videoWidth, height: v.videoHeight });
                          }}
                        />
                      </div>
                      {deleteIcon}
                    </div>
                  );
                }

                if (isPdf) {
                  return (
                    <div style={{ display: "inline-block", position: "relative", marginRight: "18px", width: "100%" }}>
                      <div style={{ border: "1px solid #e6e6e6", borderRadius: "8px" }}>
                        <embed
                          src={fileSrc}
                          type="application/pdf"
                          style={{ width: "100%", height: "400px" }}
                        />
                      </div>
                      {deleteIcon}
                    </div>
                  );
                }

                return null;
              };

              return (
                <div>
                  <div ref={fieldContainerRef}>
                    <Upload
                      disabled={shouldDisable}
                      beforeUpload={async (fileObj) => {
                        const fileType = fileObj.type?.toLowerCase() || "";
                        const fileName = fileObj.name?.toLowerCase() || "";

                        const isImage =
                          fileType.startsWith("image/") ||
                          fileName.endsWith(".png") ||
                          fileName.endsWith(".jpg") ||
                          fileName.endsWith(".jpeg") ||
                          fileName.endsWith(".webp") ||
                          fileName.endsWith(".gif");

                        const isVideo =
                          fileType.startsWith("video/") ||
                          fileName.endsWith(".mp4") ||
                          fileName.endsWith(".mov") ||
                          fileName.endsWith(".avi") ||
                          fileName.endsWith(".mkv");

                        const isPdf = fileType === "application/pdf" || fileName.endsWith(".pdf");

                        // Image validation: max 2 MB
                        if (isImage && fileObj.size > 2 * 1024 * 1024) {
                          message.error("Please add image under 2 MB");
                          return Upload.LIST_IGNORE;
                        }

                        // Image dimension validation: max 1920x800
                        if (isImage) {
                          const img: HTMLImageElement = new window.Image();
                          img.src = URL.createObjectURL(fileObj);
                          return new Promise((resolve) => {
                            img.onload = () => {
                              if (img.naturalWidth > maxWidth || img.naturalHeight > maxHeight) {
                                message.error(`Please add image under 2 MB and dimension ${maxWidth}x${maxHeight}`);
                                URL.revokeObjectURL(img.src); // Clean up
                                resolve(Upload.LIST_IGNORE);
                              } else {
                                URL.revokeObjectURL(img.src); // Clean up
                                resolve(fileObj);
                              }
                            };
                            img.onerror = () => {
                              message.error("Invalid image file");
                              resolve(Upload.LIST_IGNORE);
                            };
                          });
                        }

                        // Video validation: max 80 MB
                        if (isVideo && fileObj.size > 80 * 1024 * 1024) {
                          message.error("Please add video under 80 MB");
                          return Upload.LIST_IGNORE;
                        }

                        // PDF validation: max 5 MB
                        if (isPdf && fileObj.size > 5 * 1024 * 1024) {
                          message.error("Please add PDF under 5 MB");
                          return Upload.LIST_IGNORE;
                        }

                        return fileObj;
                      }}
                      fileList={
                        Array.isArray(controllerField.value)
                          ? controllerField.value
                          : controllerField.value
                            ? [controllerField.value]
                            : []
                      }
                      showUploadList={false}
                      accept={field?.usedFileType || ""}
                      onChange={async (info: any) => {
                        const newFile = info.fileList[info.fileList.length - 1];
                        if (newFile) {
                          const sizeBytes = newFile.originFileObj?.size ?? newFile.size ?? null;
                          setReadableSize(sizeBytes ? formatBytes(sizeBytes) : "Unknown");
                          if (!newFile.url && !newFile.preview && newFile.originFileObj) {
                            newFile.preview = URL.createObjectURL(newFile.originFileObj);
                          }
                          const newFileList = [newFile];
                          setValue(field.name, newFileList, { shouldValidate: true });
                          trigger(field.name);
                        } else {
                          setValue(field.name, "", { shouldValidate: true }); // Set to "" when fileList is empty
                          setReadableSize(null);
                          setDimensions(null);
                          trigger(field.name);
                        }
                      }}
                    >
                      <Tooltip
                        title={`Click the button & ${field.note || ""}`}
                        open={field.note ? undefined : false}
                      >
                        <Button
                          className="gap-2 h-10"
                          icon={<Icon icon="bi:upload" />}
                          disabled={shouldDisable}
                        >
                          Upload {field.label}
                        </Button>
                      </Tooltip>
                    </Upload>
                  </div>

                  {/* Dynamic Validation Message */}
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                    {isImageField
                      ? `Upload image under 2 MB with dimension ${maxWidth}x${maxHeight}`
                      : isVideoField
                        ? "Upload video under 80 MB"
                        : isPdfField
                          ? "Upload PDF under 5 MB"
                          : ""}
                  </div>
                  {hasFile && <div style={{ marginTop: "16px" }}>{renderFile()}</div>}
                  {field.note && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
        );

      case "multiFile":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...field.validation,
              validate: (value) => {
                if (!field.validation) return true;

                const files = Array.isArray(value) ? value : [];
                if (field.validation.required) {
                  const hasFile = files.some((f: any) => f?.url || f?.originFileObj);
                  if (!hasFile) {
                    return `${field.label} is required`;
                  }
                }

                for (const file of files) {
                  const fileObj = file?.originFileObj || file;
                  const fileType = fileObj?.type?.toLowerCase();
                  const fileName = fileObj?.name?.toLowerCase() || "";

                  const isImage =
                    fileType?.startsWith("image/") ||
                    fileName?.endsWith(".png") ||
                    fileName?.endsWith(".jpg") ||
                    fileName?.endsWith(".jpeg") ||
                    fileName?.endsWith(".gif") ||
                    fileName?.endsWith(".bmp") ||
                    fileName?.endsWith(".webp") ||
                    fileName?.endsWith(".tiff") ||
                    fileName?.endsWith(".jfif") ||
                    fileName?.endsWith(".svg");

                  const isVideo =
                    fileType?.startsWith("video/") ||
                    fileName.endsWith(".mp4") ||
                    fileName.endsWith(".mov") ||
                    fileName.endsWith(".avi") ||
                    fileName.endsWith(".mkv");

                  const isPdf = fileType === "application/pdf" || fileName.endsWith(".pdf");

                  const maxWidth = field.imageDimension?.width || 1920;
                  const maxHeight = field.imageDimension?.height || 800;

                  if (isImage && fileObj?.size > 2 * 1024 * 1024) {
                    return `Please add image under 2 MB and dimension ${maxWidth}x${maxHeight}`;
                  }

                  if (isVideo && fileObj?.size > 80 * 1024 * 1024) {
                    return "Please add video under 80 MB";
                  }

                  if (isPdf && fileObj?.size > 5 * 1024 * 1024) {
                    return "Please add PDF under 5 MB";
                  }
                }

                return true;
              },
            }}
            render={({ field: controllerField, fieldState }) => {
              const files = controllerField?.value || [];
              const fieldContainerRef = useRef<HTMLDivElement>(null);
              const usedFileType = field?.usedFileType?.toLowerCase() || "";
              const isImageField = usedFileType.includes("image");
              const isVideoField = usedFileType.includes("video");
              const isPdfField = usedFileType.includes("pdf");
              const maxWidth = field.imageDimension?.width || 1920; // Default to 400px if not specified
              const maxHeight = field.imageDimension?.height || 800; // Default to 260px if not specified
              useEffect(() => {
                if (!fieldState?.error || !field.validation?.required) return;

                const firstEmptyRequiredField = Object.entries(control._formValues).find(([key, value]) => {
                  const fieldDef = config.fields.find((f: FieldConfig) => f.name === key);
                  if (!fieldDef?.validation?.required) return false;

                  if (fieldDef.type === "richText") {
                    const val = Array.isArray(value) ? value.join(", ") : value || "";
                    const strippedVal = String(val)
                      ?.replace?.(/<[^>]*>/g, "")
                      ?.trim();
                    return !strippedVal;
                  }
                  if (fieldDef.type === "file" || fieldDef.type === "multiFile") {
                    const list = Array.isArray(value) ? value : [];
                    const first = list[0];
                    return !list.length || !(first?.url || first?.originFileObj);
                  }
                  if (Array.isArray(value)) return value.length === 0;
                  return value === undefined || value === null || String(value).trim() === "";
                });

                if (firstEmptyRequiredField && firstEmptyRequiredField[0] === field.name) {
                  fieldContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }, [fieldState?.error, control._formValues]);

              const handleDelete = (index: number) => {
                const file = files[index];
                if (file.preview) {
                  revokeBlobUrl(file.preview);
                }
                const newFiles = files.filter((_: any, i: number) => i !== index);
                setValue(field.name, newFiles, { shouldValidate: true });
                trigger(field.name);
              };

              const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

              const renderFile = (file: any, index: number) => {
                const fileSrc = file?.url || file?.preview;
                const fileType = file?.type?.toLowerCase();
                const fileName = file?.name?.toLowerCase();

                const isImage =
                  fileType?.startsWith("image/") ||
                  fileName?.endsWith(".png") ||
                  fileName?.endsWith(".jpg") ||
                  fileName?.endsWith(".jpeg") ||
                  fileName?.endsWith(".gif") ||
                  fileName?.endsWith(".bmp") ||
                  fileName?.endsWith(".webp") ||
                  fileName?.endsWith(".tiff") ||
                  fileName?.endsWith(".jfif") ||
                  fileName?.endsWith(".svg");

                const isVideo =
                  fileType?.startsWith("video/") ||
                  fileName?.endsWith(".mp4") ||
                  fileName?.endsWith(".mov") ||
                  fileName?.endsWith(".avi") ||
                  fileName?.endsWith(".mkv");

                const isPdf = fileType === "application/pdf" || fileName?.endsWith(".pdf");

                if (!fileSrc || (!isImage && !isVideo && !isPdf)) return null;

                // Helper to convert bytes to readable format
                const formatBytes = (bytes: number) => {
                  if (bytes === 0) return "0 Bytes";
                  const k = 1024;
                  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
                  const i = Math.floor(Math.log(bytes) / Math.log(k));
                  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
                };

                return (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      margin: "20px 20px 8px 8px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: -12,
                        right: -12,
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "50%",
                        padding: "3px",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                        zIndex: 2,
                      }}
                      onClick={() => handleDelete(index)}
                    >
                      <Icon
                        icon="mdi:close"
                        color="#333"
                        width="16"
                        height="16"
                      />
                    </div>

                    <div
                      style={{
                        width: "120px",
                        height: "120px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f9f9f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isImage && (
                        <Image
                          preview={false}
                          src={fileSrc}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "120px",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.currentTarget.src = "https://via.placeholder.com/120x120?text=Image+Error";
                          }}
                        />
                      )}

                      {isVideo && (
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          src={fileSrc}
                          onError={() => console.error("Video load failed")}
                        />
                      )}
                      {isPdf && (
                        <embed
                          src={fileSrc}
                          type="application/pdf"
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                          onError={() => console.error("PDF load failed")}
                        />
                      )}
                    </div>

                    {/* <span
                      style={{
                        marginTop: "6px",
                        fontSize: "12px",
                        color: "#555",
                        maxWidth: "120px",
                        textAlign: "center",
                        wordBreak: "break-word",
                      }}
                    >
                      {file.name}
                    </span> */}

                    {/* Display file size */}
                    {/* {file.size !== undefined && (
                      <span
                        style={{
                          fontSize: "11px",
                          color: "red",
                          marginTop: "2px",
                          maxWidth: "120px",
                          textAlign: "center",
                          wordBreak: "break-word",
                        }}
                      >
                        {file.size > MAX_FILE_SIZE ? "Please add image under 2 MB" : `${formatBytes(file.size)}`}
                      </span>
                    )} */}
                    {/* Static Format Line */}
                    {/* <span
                      style={{
                        fontSize: "11px",
                        color: "#444",
                        marginTop: "2px",
                        textAlign: "center",
                      }}
                    >
                      Format: jpg/png
                    </span> */}
                  </div>
                );
              };

              return (
                <>
                  <div ref={fieldContainerRef}>
                    <Upload
                      beforeUpload={async (file) => {
                        const fileType = file.type?.toLowerCase() || "";
                        const fileName = file.name?.toLowerCase() || "";

                        const isImage =
                          fileType.startsWith("image/") ||
                          fileName.endsWith(".png") ||
                          fileName.endsWith(".jpg") ||
                          fileName.endsWith(".jpeg") ||
                          fileName.endsWith(".webp") ||
                          fileName.endsWith(".gif");

                        // ✅ Image validation: max 2 MB
                        if (isImage && file.size > MAX_FILE_SIZE) {
                          message.error("Please add image under 2 MB");
                          return Upload.LIST_IGNORE;
                        }

                        // ✅ Image dimension check (using field.imageDimension)
                        if (isImage) {
                          const img: HTMLImageElement = new window.Image();
                          img.src = URL.createObjectURL(file);
                          return new Promise((resolve) => {
                            img.onload = () => {
                              if (img.naturalWidth > maxWidth || img.naturalHeight > maxHeight) {
                                message.error(`Please add image under 2 MB and dimension ${maxWidth}x${maxHeight}`);
                                URL.revokeObjectURL(img.src); // Clean up
                                resolve(Upload.LIST_IGNORE);
                              } else {
                                URL.revokeObjectURL(img.src); // Clean up
                                resolve(file);
                              }
                            };
                            img.onerror = () => {
                              message.error("Invalid image file");
                              resolve(Upload.LIST_IGNORE);
                            };
                          });
                        }

                        return false; // Allow file to be processed in onChange
                      }}
                      fileList={files}
                      multiple
                      showUploadList={false}
                      accept={field?.usedFileType || ""}
                      onChange={async (info: any) => {
                        const newFiles = await Promise.all(
                          info.fileList.map(async (file: any) => {
                            if (file.originFileObj && file.originFileObj.size > MAX_FILE_SIZE) {
                              return null; // Ignore file > 2MB
                            }
                            if (!file.url && !file.preview && file.originFileObj) {
                              file.preview = await createBlobUrl(file.originFileObj);
                            }
                            return file;
                          })
                        );

                        const validFiles = newFiles.filter(Boolean);
                        const newFileUids = validFiles.map((f: any) => f.uid);

                        files.forEach((f: any) => {
                          if (!newFileUids.includes(f.uid) && f.preview) {
                            revokeBlobUrl(f.preview);
                          }
                        });

                        setValue(field.name, validFiles, { shouldValidate: true });
                        trigger(field.name);
                      }}
                    >
                      <Tooltip
                        title={`Click the button & ${field.note || ""}`}
                        open={field.note ? undefined : false}
                      >
                        <Button
                          className="gap-2 h-10"
                          icon={<Icon icon="bi:upload" />}
                        >
                          Upload {field.label}
                        </Button>
                      </Tooltip>
                    </Upload>
                  </div>

                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                    {isImageField
                      ? `Upload image under 2 MB with dimension ${maxWidth}x${maxHeight}`
                      : isVideoField
                        ? "Upload video under 80 MB"
                        : isPdfField
                          ? "Upload PDF under 5 MB"
                          : ""}
                  </div>

                  {files.length > 0 && (
                    <div
                      style={{
                        marginTop: "16px",
                        display: "flex",
                        flexWrap: "wrap",
                      }}
                    >
                      {files.map(renderFile)}
                    </div>
                  )}

                  {field.note && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}

                  {/* {fieldState.error && (
                    <span
                      style={{
                        color: "red",
                        marginTop: "8px",
                        display: "block",
                      }}
                    >
                      {fieldState.error.message}
                    </span>
                  )} */}
                </>
              );
            }}
          />
        );

      case "select":
        return (
          <Controller
            name={field.name}
            control={control}
            // rules={field.validation}
            rules={{
              ...field.validation,
              required: shouldDisable ? false : field.validation?.required,
            }}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = Array.isArray(field.options)
                ? field.options
                : Array.isArray(options[field.name])
                  ? options[field.name]
                  : [];

              console.log("selectedoptons for dropdwon", selectOptions, controllerField?.value);

              const selectedValue =
                field.name === "epcStatus" &&
                [
                  "Pending Approval (New Upgrade)",
                  "Pending Approval (New Renewal)",
                  "Pending Approval (New EPC)",
                ].includes(controllerField.value)
                  ? undefined
                  : controllerField.value;

              return (
                <div>
                  <Select
                    size="large"
                    {...controllerField}
                    value={selectedValue || controllerField?.value}
                    placeholder={field.placeholder}
                    // disabled={field.editable === false}
                    disabled={field.editable === false || shouldDisable}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    onSearch={field.onSearch}
                    getPopupContainer={getContainer}
                    onChange={(value) => {
                      if (
                        field.name === "epcStatus" &&
                        [
                          "Pending Approval (New Upgrade)",
                          "Pending Approval (New Renewal)",
                          "Pending Approval (New EPC)",
                        ].includes(value)
                      ) {
                        setValue("epcStatus", null);
                      } else {
                        controllerField.onChange(value);
                      }

                      if (field.name === "field_module") {
                        setValue("field_sub_module", "");
                      }

                      if (field.onChange) {
                        field.onChange(
                          selectOptions.find((option) => option.value === value),
                          { setValue, getValues }
                        );
                      }
                    }}
                  >
                    {selectOptions.map((option) => (
                      <Option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                  {field.note && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
        );

      case "multiSelect":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = Array.isArray(field.options)
                ? field.options
                : Array.isArray(options[field.name])
                  ? options[field.name]
                  : [];

              const selectedValues = Array.isArray(controllerField.value)
                ? controllerField.value
                : controllerField.value
                  ? [controllerField.value]
                  : [];

              return (
                <Select
                  mode="multiple"
                  size="large"
                  {...controllerField}
                  value={selectedValues}
                  placeholder={field.placeholder}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                  getPopupContainer={getContainer}
                  onChange={(values) => {
                    controllerField.onChange(values);

                    if (field.onChange) {
                      const selectedOptions = selectOptions.filter((option) => values.includes(option.value));
                      field.onChange(selectedOptions, { setValue, getValues });
                    }
                  }}
                  maxTagCount={undefined}
                  dropdownStyle={{ minWidth: 200 }}
                >
                  {selectOptions.map((option) => (
                    <Option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Option>
                  ))}
                </Select>
              );
            }}
          />
        );

      case "date":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const today = new Date().toISOString().split("T")[0];
              return (
                <Input
                  {...controllerField}
                  type="date"
                  className="h-10"
                  placeholder={field.placeholder}
                  max={field.futureDateRestriction ? today : ""}
                  disabled={field.editable === false}
                  onChange={(e) => controllerField.onChange(e.target.value)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.blur();
                    setTimeout(() => e.currentTarget.focus(), 0);
                    e.currentTarget.showPicker();
                  }}
                />
              );
            }}
          />
        );

      // case "richText":
      //   return (
      //     <Controller
      //       name={field.name}
      //       control={control}
      // rules={{
      //   ...field.validation,
      //   validate: (value) => {
      //     console.log("rtevalue", value);

      //     if (field?.validation?.required) {
      //       const requiredMessage =
      //         typeof field.validation.required === "object"
      //           ? field.validation.required.message || "This field is required"
      //           : field.validation.required;

      //       // Handle undefined, null, or empty string
      //       if (value == null || value === "") {
      //         return requiredMessage;
      //       }

      //       // Handle string type
      //       if (typeof value === "string") {
      //         const strippedValue = value.replace(/<[^>]*>/g, "").trim();

      //         if (!strippedValue) {
      //           return requiredMessage;
      //         }

      //         // Extract maxLength value if it exists
      //         const maxLength = field.validation.maxLength
      //           ? typeof field.validation.maxLength === "object"
      //             ? field.validation.maxLength.value
      //             : field.validation.maxLength
      //           : undefined;

      //         // Handle maxLength validation
      //         if (maxLength !== undefined && strippedValue.length > maxLength) {
      //           return `Maximum ${maxLength} characters allowed`;
      //         }

      //         return true;
      //       }

      //       // Handle array type
      //       if (Array.isArray(value)) {
      //         if (value.length === 0) {
      //           return requiredMessage;
      //         }
      //         return true;
      //       }

      //       // For other types, return a generic error or accept them
      //       return "Invalid value type";
      //     }

      //     return true;
      //   },
      // }}
      //     rules={field.validation}
      //     render={({ field: controllerField }) => {
      //       const value = Array.isArray(controllerField.value)
      //         ? controllerField.value.join(", ")
      //         : controllerField.value || "";
      //       const extractUrlFromParagraph = (text: string) => {
      //         const match = text.match(/<p>(https?:\/\/[^\s]+)<\/p>/);
      //         return match ? match[1] : null;
      //       };
      //       const imageUrl = extractUrlFromParagraph(value);
      //       return (
      //         <>
      //           <RichTextEditor
      //             // value={controllerField.value || ""}
      //             value={imageUrl ? `<img src="${imageUrl}" alt="image" />` : value}
      //             onChange={controllerField.onChange}
      //             placeholder={field.placeholder}
      //           />
      //           {field.note && (
      //             <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
      //               <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
      //               <span>{field.note}</span>
      //             </div>
      //           )}
      //         </>
      //       )
      //     }}
      //   />
      // );
      //       case "richText":
      //         return (
      //           <Controller
      //             name={field.name}
      //             control={control}
      //             rules={{
      //               validate: (value) => {
      //                 if (!value) {
      //                   return (
      //                     (typeof field.validation?.required === "object"
      //                       ? field.validation.required.message
      //                       : field.validation?.required) || "This field is required"
      //                   );
      //                 }

      //                 const strippedValue = value.replace(/<[^>]*>/g, "").trim();
      //                 return strippedValue ? true : "This field is required";
      //               },
      //             }}
      //             render={({ field: controllerField, fieldState: { error } }) => {
      //               const fieldContainerRef = useRef<HTMLDivElement>(null);

      //               useEffect(() => {
      //                 if (!error || !field.validation?.required) return;

      //                 // Find the first empty required field
      //                 const firstEmptyRequiredField = Object.entries(control._formValues).find(([key, value]) => {
      //                   // use config.fields instead of allFields
      //                   const fieldDef = config.fields.find((f: FieldConfig) => f.name === key);
      //                   if (!fieldDef?.validation?.required) return false;

      //                   const val = Array.isArray(value) ? value.join(", ") : value || "";
      //                   const strippedVal = val.replace(/<[^>]*>/g, "").trim();
      //                   return !strippedVal;
      //                 });

      //                 // Scroll only if current field is the first empty required field
      //                 if (firstEmptyRequiredField && firstEmptyRequiredField[0] === field.name) {
      //                   fieldContainerRef.current?.scrollIntoView({
      //                     behavior: "smooth",
      //                     block: "center",
      //                   });
      //                 }
      //               }, [error, control._formValues]);

      //               const isRequiredError = !!(error && field.validation?.required);

      //               const value = Array.isArray(controllerField.value)
      //                 ? controllerField.value.join(", ")
      //                 : controllerField.value || "";

      //               const extractUrlFromParagraph = (text: string) => {
      //                 const match = text.match(/<p>(https?:\/\/[^\s]+)<\/p>/);
      //                 return match ? match[1] : null;
      //               };

      //               const imageUrl = extractUrlFromParagraph(value);

      //               return (
      //                 <div>
      //                   <div
      //                     ref={fieldContainerRef}
      //                     style={{
      //                       border: isRequiredError ? "1px solid red" : "#ccc",
      //                       borderRadius: "4px",
      //                       padding: "6px",
      //                     }}
      //                   >
      //                     <RichTextEditor
      //                       value={imageUrl ? `<img src="${imageUrl}" alt="image" />` : value}
      //                       onChange={controllerField.onChange}
      //                       placeholder={field.placeholder}
      //                     />
      //                   </div>

      //                   {/* Error message for required field */}
      //                   {/* {error && (
      //   <p
      //     style={{
      //       color: "red",
      //       marginTop: "4px",
      //       fontSize: "12px",
      //     }}
      //   >
      //     {error.message}
      //   </p>
      // )} */}

      //                   {field.note && (
      //                     <div
      //                       style={{
      //                         marginTop: "8px",
      //                         color: "#666",
      //                         display: "flex",
      //                         alignItems: "center",
      //                         gap: "6px",
      //                       }}
      //                     >
      //                       <Icon
      //                         icon="mdi:information-outline"
      //                         color="#666"
      //                         width="16"
      //                         height="16"
      //                       />
      //                       <span>{field.note}</span>
      //                     </div>
      //                   )}
      //                 </div>
      //               );
      //             }}
      //           />
      //         );
      // case "richText":
      //   return (
      //     <Controller
      //       name={field.name}
      //       control={control}
      //       rules={{
      //         validate: (value) => {
      //           if (!field.validation?.required) return true;

      //           if (!value) {
      //             return (
      //               (typeof field.validation?.required === "object"
      //                 ? field.validation.required.message
      //                 : field.validation?.required) || ""
      //             );
      //           }

      //           const strippedValue = value.replace(/<[^>]*>/g, "").trim();
      //           return strippedValue ? true : "This field is required";
      //         },
      //       }}
      //       render={({ field: controllerField, fieldState: { error } }) => {
      //         const fieldContainerRef = useRef<HTMLDivElement>(null);

      //         useEffect(() => {
      //           if (!error || !field.validation?.required) return;

      //           const firstEmptyRequiredField = Object.entries(control._formValues).find(([key, value]) => {
      //             const fieldDef = config.fields.find((f: FieldConfig) => f.name === key);
      //             if (!fieldDef?.validation?.required) return false;

      //             const val = Array.isArray(value) ? value.join(", ") : value || "";
      //             const strippedVal = val.replace(/<[^>]*>/g, "").trim();
      //             return !strippedVal;
      //           });

      //           if (firstEmptyRequiredField && firstEmptyRequiredField[0] === field.name) {
      //             fieldContainerRef.current?.scrollIntoView({
      //               behavior: "smooth",
      //               block: "center",
      //             });
      //           }
      //         }, [error, control._formValues]);

      //         const isRequiredError = !!(error && field.validation?.required);

      //         const value = Array.isArray(controllerField.value)
      //           ? controllerField.value.join(", ")
      //           : controllerField.value || "";

      //         const extractUrlFromParagraph = (text: string) => {
      //           const match = text.match(/<p>(https?:\/\/[^\s]+)<\/p>/);
      //           return match ? match[1] : null;
      //         };

      //         const imageUrl = extractUrlFromParagraph(value);

      //         return (
      //           <div>
      //             <div
      //               ref={fieldContainerRef}
      //               style={{
      //                 border: isRequiredError ? "1px solid red" : "#ccc",
      //                 borderRadius: "4px",
      //                 padding: "6px",
      //               }}
      //             >
      //               <RichTextEditor
      //                 value={imageUrl ? `<img src="${imageUrl}" alt="image" />` : value}
      //                 onChange={controllerField.onChange}
      //                 placeholder={field.placeholder}
      //               />
      //             </div>

      //             {/* {isRequiredError && (
      //         <p
      //           style={{
      //             color: "red",
      //             marginTop: "4px",
      //             fontSize: "12px",
      //           }}
      //         >
      //           {error.message}
      //         </p>
      //       )} */}

      //             {field.note && (
      //               <div
      //                 style={{
      //                   marginTop: "8px",
      //                   color: "#666",
      //                   display: "flex",
      //                   alignItems: "center",
      //                   gap: "6px",
      //                 }}
      //               >
      //                 <Icon
      //                   icon="mdi:information-outline"
      //                   color="#666"
      //                   width="16"
      //                   height="16"
      //                 />
      //                 <span>{field.note}</span>
      //               </div>
      //             )}
      //           </div>
      //         );
      //       }}
      //     />
      //   );
      case "richText":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              validate: (value) => {
                if (shouldDisable) return true;

                if (!field.validation?.required) return true;

                if (!value) {
                  return (
                    (typeof field.validation?.required === "object"
                      ? field.validation.required.message
                      : field.validation?.required) || ""
                  );
                }

                const strippedValue = value?.replace?.(/<[^>]*>/g, "")?.trim();
                return strippedValue ? true : "This field is required";
              },
            }}
            render={({ field: controllerField, fieldState: { error } }) => {
              const fieldContainerRef = useRef<HTMLDivElement>(null);

              useEffect(() => {
                if (shouldDisable) return;

                if (!error || !field.validation?.required) return;

                const firstEmptyRequiredField = Object.entries(control._formValues).find(([key, value]) => {
                  const fieldDef = config.fields.find((f: FieldConfig) => f.name === key);
                  if (!fieldDef?.validation?.required) return false;

                  const val = Array.isArray(value) ? value.join(", ") : value || "";
                  const strippedVal = val?.replace?.(/<[^>]*>/g, "")?.trim();
                  return !strippedVal;
                });

                if (firstEmptyRequiredField && firstEmptyRequiredField[0] === field.name) {
                  fieldContainerRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }, [error, control._formValues]);

              // const isRequiredError = !!(error && field.validation?.required);

              const isRequiredError = !shouldDisable && !!(error && field.validation?.required);

              const value = Array.isArray(controllerField.value)
                ? controllerField.value.join(", ")
                : controllerField.value || "";

              // Helper function to decode Unicode escapes
              const decodeUnicode = (str: string): string => {
                return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => String.fromCharCode(parseInt(code, 16)));
              };

              // Helper function to decode double-encoded HTML
              // const decodeHtml = (str: string): string => {
              //   const txt = document.createElement("textarea");
              //   txt.innerHTML = str;
              //   return txt.value;
              // };
              const decodeHtml = (str: string): string => {
                if (!str) return "";
                // Use a safer way to decode entities without parsing tags as DOM
                return str
                  .replace(/&lt;/g, "<")
                  .replace(/&gt;/g, ">")
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'")
                  .replace(/&amp;/g, "&");
              };

              // Check if value needs cleaning
              const needsCleaning = (text: string): boolean => {
                return (
                  text.includes("\\u") ||
                  text.includes("&lt;") ||
                  text.includes("&gt;") ||
                  text.includes('<a href="') ||
                  /<img\s+src="<a\s+href=/i.test(text)
                );
              };

              // Clean and decode the value
              const cleanValue = (text: string): string => {
                if (!text || !needsCleaning(text)) return text;

                // First decode Unicode escapes
                let decoded = decodeUnicode(text);

                // Then decode HTML entities (possibly twice if double-encoded)
                decoded = decodeHtml(decoded);
                if (decoded.includes("&lt;") || decoded.includes("&gt;")) {
                  decoded = decodeHtml(decoded);
                }

                // Remove <a href> tags and extract just the URL
                // decoded = decoded.replace(/<a\s+href="([^"]+)"[^>]*>([^<]*)<\/a>/gi, "$1");

                // Fix malformed img tags where <a href> is inside src attribute
                decoded = decoded.replace(
                  /<img\s+src="<a\s+href="([^"]+)"[^>]*>.*?<\/a>"[^>]*>/gi,
                  '<img src="$1" alt="Image" />'
                );

                // Fix other malformed img tags with semicolons or ampersands
                decoded = decoded.replace(/<img\s+src="([^"<>\s]+)[^"]*"[^>]*>/gi, (match, url) => {
                  const cleanUrl = url.split(";")[0].split("&")[0].trim();
                  return `<img src="${cleanUrl}" alt="Image" />`;
                });

                return decoded;
              };

              const extractUrlFromParagraph = (text: string) => {
                const match = text.match(/<p>(https?:\/\/[^\s]+)<\/p>/);
                return match ? match[1] : null;
              };

              // Clean value only if it needs cleaning
              // const cleanedValue = cleanValue(value);
              const cleanedValue = shouldDisable ? value : cleanValue(value);
              const imageUrl = extractUrlFromParagraph(cleanedValue);

              const handleChange = (val: string) => {
                if (shouldDisable) return;
                // Don't clean on every change, only validate
                const stripped = val?.replace?.(/<[^>]*>/g, "")?.trim();
                controllerField.onChange(stripped ? val : "");
              };

              return (
                <div>
                  <div
                    ref={fieldContainerRef}
                    // style={{
                    //   border: isRequiredError ? "1px solid red" : "#ccc",
                    //   borderRadius: "4px",
                    //   padding: "6px",
                    // }}

                    style={{
                      border: isRequiredError ? "1px solid red" : "#ccc",
                      borderRadius: "4px",
                      padding: "6px",
                      opacity: shouldDisable ? 0.6 : 1,
                      pointerEvents: shouldDisable ? "none" : "auto",
                      background: shouldDisable ? "#f5f5f5" : "#fff",
                    }}
                  >
                    <RichTextEditor
                      value={imageUrl ? `<img src="${imageUrl}" alt="image" />` : cleanedValue}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      disabled={shouldDisable}
                    />
                  </div>

                  {field.note && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="16"
                        height="16"
                      />
                      <span>{field.note}</span>
                    </div>
                  )}
                </div>
              );
            }}
          />
        );

      case "time":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const defaultStart = moment("00:00:00", "HH:mm:ss");
              const defaultEnd = moment("00:00:00", "HH:mm:ss");

              const parseValue = (value: string) => {
                if (!value) return [defaultStart, defaultEnd];
                const [start, end] = value.split(" - ");
                return [start ? moment(start, "HH:mm:ss") : defaultStart, end ? moment(end, "HH:mm:ss") : defaultEnd];
              };

              const formatValue = (value: [moment.Moment, moment.Moment]) => {
                return `${value[0].format("HH:mm:ss")} - ${value[1].format("HH:mm:ss")}` || "00:00:00 - 00:00:00";
              };

              const [startTime, endTime] = parseValue(controllerField.value as string);

              return (
                <TimePicker.RangePicker
                  {...controllerField}
                  format="HH:mm:ss"
                  className="h-10"
                  onChange={(value) => {
                    controllerField.onChange(
                      value
                        ? formatValue([value[0] || defaultStart, value[1] || defaultEnd])
                        : formatValue([defaultStart, defaultEnd])
                    );
                  }}
                  value={[startTime, endTime]}
                />
              );
            }}
          />
        );
      case "dateTime":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              return (
                <DatePicker
                  {...controllerField}
                  format="DD/MM/YYYY HH:mm:ss"
                  className="h-10 w-100"
                  showTime={{ format: "HH:mm:ss" }}
                  value={controllerField.value ? moment(controllerField.value) : null}
                  disabledDate={(current) => {
                    if (!field.disabledPreviousDateTime) return false;
                    // Disable past dates (strictly before now)
                    return current && current < moment().startOf("day");
                  }}
                  disabledTime={(date) => {
                    if (!field.disabledPreviousDateTime || !date || !date.isSame(moment(), "day")) {
                      return {};
                    }
                    const now = moment();
                    if (date.isSame(now, "day")) {
                      return {
                        disabledHours: () =>
                          Array.from({ length: 24 }, (_, i) => (i < now.hour() ? i : -1)).filter((h) => h !== -1),
                        disabledMinutes: () =>
                          Array.from({ length: 60 }, (_, i) =>
                            date.hour() === now.hour() && i < now.minute() ? i : -1
                          ).filter((m) => m !== -1),
                        disabledSeconds: () =>
                          Array.from({ length: 60 }, (_, i) =>
                            date.hour() === now.hour() && date.minute() === now.minute() && i < now.second() ? i : -1
                          ).filter((s) => s !== -1),
                      };
                    }
                    return {};
                  }}
                  onChange={(date: any) => {
                    controllerField.onChange(date ? moment(date).format("YYYY-MM-DDTHH:mm:ss") : null);
                  }}
                />
              );
            }}
          />
        );

      case "dateFormat":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              return (
                <DatePicker
                  {...controllerField}
                  format="DD/MM/YYYY"
                  className="h-10"
                  value={controllerField.value ? moment(controllerField.value) : null}
                  disabledDate={(current) => {
                    // Disable past dates (strictly before today)
                    if (!field.disabledPreviousDateTime) return false;
                    return current && current < moment().startOf("day");
                  }}
                  onChange={(date: any) => {
                    controllerField.onChange(date ? moment(date).format("YYYY-MM-DD") : null);
                  }}
                />
              );
            }}
          />
        );

      case "multicheckbox":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={[]}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div className={field.className}>
                {field.options?.map((option) => (
                  <Checkbox
                    key={option.value}
                    value={option.value}
                    checked={controllerField.value?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentValue = controllerField.value || [];
                      let updatedValue;

                      if (e.target.checked) {
                        updatedValue = [...currentValue, option.value];
                      } else {
                        updatedValue = currentValue.filter((v: string) => v !== option.value);
                      }
                      controllerField.onChange(updatedValue.length > 0 ? updatedValue : null);
                    }}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </div>
            )}
          />
        );
      case "radio":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div className={field.className}>
                {field.options?.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    checked={controllerField.value === option.value}
                    onChange={(e) => controllerField.onChange(e.target.value)}
                  >
                    {option.label}
                  </Radio>
                ))}
              </div>
            )}
          />
        );
      case "radioWithDropdown":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = field.loadOptions ? options[field.name] || [] : field.options || [];
              const [otherValue, setOtherValue] = React.useState("");
              const [inputValue, setInputValue] = React.useState("");
              React.useEffect(() => {
                if (controllerField.value !== "Malaysian") {
                  setOtherValue(controllerField.value);
                }
              }, []);
              React.useEffect(() => {
                if (controllerField.value === "Malaysian") {
                  const nricValue = getValues("nric");
                  if (nricValue?.length > 12) {
                    setError("NRIC number cannot exceed 12 digits");
                  } else {
                    setError("");
                  }
                  setInputValue(nricValue || "");
                }
              }, [controllerField.value, getValues]);

              const handleRadioChange = (e: RadioChangeEvent) => {
                const selectedValue = e.target.value;
                if (selectedValue !== "Other") {
                  setOtherValue("");
                }
                controllerField.onChange(selectedValue);
              };

              const handleDropdownChange = (value: string) => {
                setOtherValue(value);
                controllerField.onChange(value);
              };

              const handleChange = (e: any) => {
                const value = e.target.value.replace?.(/\D/g, "");
                setInputValue(value);

                if (controllerField.value !== "Other") {
                  setValue("nric", value);
                }
                if (value?.length > 12) {
                  setError("NRIC number cannot exceed 12 digits");
                } else {
                  setError("");
                }
              };
              return (
                <div className={field.className}>
                  {field.options?.map((option) => {
                    console.log(
                      "optionsssssssssssssssssss",
                      controllerField.value,
                      option.value,
                      field.options?.some((opt) => opt.value === controllerField.value)
                    );
                    return (
                      <div
                        key={option.value}
                        style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
                      >
                        <Radio
                          value={option.value}
                          checked={(() => {
                            if (
                              controllerField.value === "" ||
                              controllerField.value === null ||
                              controllerField.value === undefined
                            ) {
                              return false;
                            }
                            if (option.value === "Other") {
                              const isValueNotInOptions = !field.options?.some(
                                (opt) => opt.value === controllerField.value
                              );
                              return isValueNotInOptions || controllerField.value === "Other";
                            }
                            return controllerField.value === option.value;
                          })()}
                          onChange={handleRadioChange}
                        >
                          {option.label}
                        </Radio>
                        {option.value !== "Other" && controllerField.value === "Malaysian" && (
                          <Form.Item
                            validateStatus={inputValue.length > 12 ? "error" : ""}
                            help={inputValue.length > 12 ? "NRIC number cannot exceed 12 digits" : ""}
                            // style={{ marginBottom: 0 }}
                          >
                            <Input
                              value={inputValue || ""}
                              className="h-10"
                              style={{ width: 250, marginLeft: 10 }}
                              placeholder="Enter NRIC Number"
                              onChange={(e) => {
                                let value = e.target.value?.replace?.(/\D/g, "");
                                setInputValue(value);
                                handleChange({ target: { value } });
                              }}
                            />
                          </Form.Item>
                        )}
                        {controllerField.value &&
                          option.value === "Other" &&
                          (!field.options?.some((opt) => opt.value === controllerField.value) ||
                            controllerField.value === "Other") && (
                            <Select
                              size="large"
                              style={{ width: 200, marginLeft: 10 }}
                              placeholder={field.placeholder}
                              showSearch
                              getPopupContainer={getContainer}
                              value={otherValue || undefined}
                              onChange={handleDropdownChange}
                            >
                              {selectOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          )}
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        );
      case "color":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div>
                <SketchPicker
                  color={controllerField.value}
                  onChange={(color: any) => controllerField.onChange(color.hex)}
                />
                {/* <Input
                        style={{ marginTop: 8 }}
                        value={controllerField.value}
                        readOnly
                    /> */}
                <div style={{ marginTop: 8, fontWeight: "bold" }}>
                  Selected Color: <span style={{ color: controllerField.value }}>{controllerField.value}</span>
                </div>
              </div>
            )}
          />
        );
      case "dropdownWithInput":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = field.loadOptions ? options[field.name] || [] : field.options || [];
              const [selectedOption, setSelectedOption] = React.useState<string>("");
              const [customInput, setCustomInput] = React.useState("");

              // Initialize state on mount or when controllerField.value changes
              React.useEffect(() => {
                if (controllerField.value) {
                  const isOptionExist = selectOptions.some((opt) => opt.value === controllerField.value);
                  if (isOptionExist) {
                    setSelectedOption(controllerField.value);
                    setCustomInput("");
                  } else {
                    setSelectedOption("Other");
                    setCustomInput(controllerField.value);
                  }
                }
              }, [controllerField.value]);

              const handleSelectChange = (value: string) => {
                setSelectedOption(value);
                if (value !== "Other") {
                  setCustomInput("");
                  controllerField.onChange(value);
                } else {
                  controllerField.onChange(""); // Clear the form value when "Other" is selected
                }
              };

              const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setCustomInput(value);
                controllerField.onChange(value);
              };

              return (
                <div className={field.className}>
                  <Select
                    size="large"
                    style={{ width: "100%", marginBottom: selectedOption === "Other" ? 8 : 0 }}
                    placeholder={field.placeholder}
                    getPopupContainer={getContainer}
                    showSearch
                    value={selectedOption || undefined}
                    onChange={handleSelectChange}
                  >
                    {selectOptions.map((option) => (
                      <Option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </Option>
                    ))}
                    <Option value="Other">Other</Option>
                  </Select>

                  {selectedOption === "Other" && (
                    <Input
                      placeholder="Please specify"
                      value={customInput}
                      onChange={handleInputChange}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              );
            }}
          />
        );
      case "dateWithCheckbox":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              console.log("fieldValidatonsss", field.validation, controllerField);
              const today = new Date().toISOString().split("T")[0];
              const epcStatus = watch("epcStatus");
              const epcType = watch("epcType");
              const isForRenewal = watch("isForRenewal") ?? false;
              const [checkBoxEnable, setCheckBoxEnable] = useState(false);
              const [fieldName, setFieldName] = useState("");
              const [label, setLabel] = useState("Renewal");
              const isEditable = epcType === "PRIORITY" ? true : field.editable;
              useEffect(() => {
                if (epcStatus && !CreatingNewOne && epcType !== "PRIORITY") {
                  setFieldName("isForRenewal");
                  setCheckBoxEnable(true);
                  setLabel("Renewal");
                }
              }, [epcStatus, epcType]);

              return (
                // <div className="flex gap-2 items-center">
                <div className="space-y-2">
                  <Input
                    {...controllerField}
                    type="date"
                    className="h-10"
                    placeholder={field.placeholder}
                    max={field.futureDateRestriction ? today : ""}
                    disabled={!isEditable}
                    onChange={(e) => {
                      controllerField.onChange(e.target.value);
                      controllerField.onBlur();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.currentTarget.blur();
                      setTimeout(() => e.currentTarget.focus(), 0);
                      e.currentTarget.showPicker();
                    }}
                  />
                  {checkBoxEnable && (
                    <Checkbox
                      checked={Boolean(isForRenewal)}
                      onChange={(e) => setValue(fieldName, e.target.checked ?? false)}
                    >
                      {label}
                    </Checkbox>
                  )}
                </div>
              );
            }}
          />
        );
      case "dynamicRow":
        return (
          <DynamicFormArray
            field={field}
            initialValues={config.initialValues[field.name] || []}
            onDataChange={handleDynamicFormArrayChange(field.name)}
          />
        );
      default:
        return null;
    }
  };

  // const handleFormSubmit = (data: FieldValues, event: any) => {
  //   const formattedData = { ...data };
  //   Object.keys(data).forEach(key => {
  //     if (typeof data[key] === 'object' && !Array.isArray(data[key])) {
  //       formattedData[key] = Object.entries(data[key]).map(([id, value]) => ({
  //         id,
  //         ...value
  //       }));
  //     }
  //   });

  //   setConfirmModalVisible(true);
  //   setFormData(formattedData);
  // };

  // const handleFormSubmit = (data: FieldValues, event:any) => {
  //   const formattedData = { ...data };

  //   // Iterate over dynamic rows
  //   Object.keys(dynamicRows).forEach((fieldName) => {
  //     // Find the corresponding field configuration
  //     const field = config.fields.find((f) => f.name === fieldName);

  //     if (field && field.rowConfig) {
  //       // Map each row to its data
  //       formattedData[fieldName] = dynamicRows[fieldName].map((row) => {
  //         const rowData: any = {};

  //         // Populate row data using nested fields
  //         // field.rowConfig.fields.forEach((nestedField: FieldConfig) => {
  //         //   rowData[nestedField.name] = data[`${nestedField.name}-${row.id}`];
  //         // });

  //         return rowData;
  //       });
  //     }
  //   });

  //   setConfirmModalVisible(true);
  //   setFormData(formattedData);
  // };

  const handleFormSubmit = (data: FieldValues, event: any) => {
    setConfirmModalVisible(true);
    setFormData(data);
  };

  const handleConfirmSubmit = async (reviewNotes: string) => {
    console.log("dynamicform-confirmdata", formData);
    try {
      if (hideButton) {
        await handleSubmit((data) => {
          console.log("dynamicform-confirmdata", data);
          finalData(data);
        })();
      } else {
        if (button === "save") {
          const currentFormValues = getValues();
          console.log("dynamicform-draft-values", currentFormValues);
          await onSubmit(currentFormValues, button);
          // await onSubmit(formData, button);
        } else {
          await handleSubmit((data) => {
            console.log("dynamicform-confirmdata", data);
            onSubmit(data, button, reviewNotes);
          })();
        }
      }
    } catch (err) {
      // TODO: Handle Error
    }
  };

  return (
    <>
      {isPreviewVisible && previewContent && (
        <div
          className="mb-5 relative text-white p-4 rounded-lg shadow-md"
          style={{ backgroundColor: "#dab2ec" }}
        >
          <div className="flex items-center justify-between">
            <div className="font-medium text-lg">{previewContent}</div>
            <div className="flex-shrink-0">
              <button
                className="p-2 rounded-full focus:outline-none"
                style={{
                  backgroundColor: "#b284d6",
                  color: "white",
                }}
                onClick={() => setIsPreviewVisible(false)}
              >
                <Icon
                  icon="mdi:close"
                  className="text-xl"
                />
              </button>
            </div>
          </div>
        </div>
      )}
      <Form
        className="grid grid-cols-12 gap-4 w-full"
        layout="vertical"
        onFinish={handleSubmit(handleFormSubmit)}
        // onFinish={() => {
        //   setConfirmModalVisible(true);
        // }}
      >
        {config?.fields?.map((field) => {
          // const isRequired = Boolean(field?.validation?.required);
          const isRequired =
            field.name === "passportNumber" || field.name === "country" || field.name === "state"
              ? watchEpcType !== "PRIORITY"
              : field.name === "profession" && watchEpcType === "PRIORITY"
                ? true
                : Boolean(field?.validation?.required);
          const maxLength = typeof field?.validation?.maxLength === "number" ? field.validation.maxLength : null;
          console.log("renderField", field);
          if (field.showField && !field.showField(getValues())) {
            return null;
          }
          return (
            <Form.Item
              className={field.className}
              hidden={field.hidden || false}
              key={field.name}
              required={isRequired}
              label={
                field.type !== "checkbox" ? (
                  <div className="flex items-center gap-2">
                    <span>{field.label}</span>

                    {/* Show info icon only for inputNotes */}
                    {field.type === "inputNotes" && (
                      <Icon
                        icon="mdi:information-outline"
                        color="#666"
                        width="18"
                        height="18"
                        className="cursor-pointer hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation(); // prevent form item click
                          field.openUpload?.(field.name, setValue);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  ""
                )
              }
              validateStatus={button !== "save" && get(errors, field.name) ? "error" : ""}
              help={
                button !== "save" ? (
                  <div>
                    {get(errors, `${field.name}.message`)?.toString() ||
                      (maxLength ? `Maximum ${maxLength} characters allowed` : "")}
                  </div>
                ) : null
              }
            >
              {renderField(field)}
            </Form.Item>
          );
        })}

        {hideButton && (
          <Form.Item className="col-span-12 gap-5">
            <div className="flex gap-5 flex-row-reverse">
              <Button
                type="primary"
                htmlType="submit"
                onClick={(e) => {
                  console.log("erroringggg", e, error);
                  if (error) {
                    e.preventDefault();
                    return;
                  }
                  setButton("submitWithNoReview");
                }}
              >
                Submit
              </Button>
            </div>
          </Form.Item>
        )}

        {!hideButton && (
          <Form.Item className="col-span-12 gap-5">
            <div className="flex gap-5 flex-row-reverse">
              <Button
                type="primary"
                htmlType="submit"
                onClick={(e) => {
                  console.log("erroringggg", e, error);
                  if (error) {
                    e.preventDefault();
                    return;
                  }
                  setButton("submit");
                }}
              >
                Submit for Review
              </Button>
              <Button
                type="ghost"
                htmlType="submit"
                onClick={(e) => {
                  e.preventDefault();
                  setButton("save");

                  const currentValues = getValues();
                  const hasValues = Object.values(currentValues).some((val) => {
                    return (
                      !Array.isArray(val) && typeof val !== "boolean" && val !== undefined && val !== null && val !== ""
                    );
                  });

                  const hasValidFiles = Object.entries(currentValues).some(([key, value]) => {
                    if (Array.isArray(value)) {
                      return value.some((file) => {
                        return !isEmpty(file?.originFileObj) || file?.url;
                      });
                    }
                    return false;
                  });

                  console.log("currentValues", currentValues, { hasValues, hasValidFiles });

                  if (!hasValues && !hasValidFiles) {
                    openToast({
                      content: "Please fill at least one field before saving as draft.",
                      type: "warning",
                    });
                    return;
                  }

                  handleFormSubmit(currentValues, e);
                }}
              >
                Save as Draft
              </Button>
            </div>
          </Form.Item>
        )}
      </Form>

      <ConfirmationModal
        open={confirmModalVisible}
        onClose={() => setConfirmModalVisible(false)}
        onSubmit={handleConfirmSubmit}
        type={button}
        defaultReviewNotes={defaultReviewNotes}
      />
    </>
  );
};
